import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, first, Observable, Subject, take, takeUntil } from 'rxjs';

import { UaService } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { RouterService, TitleService } from '@ecm/router';
import { TMSService } from '@ecm/tracker';

import { MenuViewModel } from '../model/menu.view-model';

@Injectable({
    providedIn: 'root',
})
export class MenuStoreService {
    private _menus$ = new BehaviorSubject<MenuViewModel[]>([]);

    private _menuCollapsed$ = new BehaviorSubject<boolean>(true);

    private _showBack$ = new BehaviorSubject<boolean>(false);

    private _backRoute: string;

    private _pathMatchingMap: Map<string, string[]> = new Map<string, string[]>([
        ['accueil', ['conso', 'equipements', 'usage']],
        ['contrats-options', ['offre-options', 'contrat']],
        ['factures-paiement', ['facture-paiement']],
        ['compte', ['compte']],
    ]);

    private _unsubscribe$: Subject<void>;

    public constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        private translate: TranslateService,
        protected uaService: UaService,
        protected titleService: TitleService,
        protected routerService: RouterService,
        protected router: Router,
        protected route: ActivatedRoute,
        private tmsService: TMSService,
    ) {}

    //////////////////////
    //  COMMANDS
    //////////////////////

    public enter(): void {
        this._unsubscribe$ = new Subject<void>();
        this.router.events
            .pipe(
                takeUntil(this._unsubscribe$),
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            )
            .subscribe(() => this.updateViewModel());
        this.translate
            .get('ecm.menu')
            .pipe(take(1))
            .subscribe((menuTranslation: any) => {
                if (menuTranslation && 'sections' in menuTranslation) {
                    this._menus$.next(menuTranslation.sections);
                    this.updateViewModel();
                }
            });
        EcmDispatcherModule.getBackRoute()
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((route: string) => {
                this._backRoute = route;
                this._showBack$.next(!!route);
            });
    }

    public leave(): void {
        this._menus$.next([]);
        this._backRoute = undefined;
        this._showBack$.next(false);
        this._menuCollapsed$.next(true);
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    public selectMenuItem(menu: MenuViewModel): void {
        this._menuCollapsed$.next(true);
        this.trackTmsLink(menu.label, this.router.createUrlTree([menu.path]).toString());
    }

    public switchCollapse(): void {
        this._menuCollapsed$.next(!this._menuCollapsed$.value);
    }

    public navigateToBackRoute(): void {
        if (this._backRoute === null || this._backRoute === undefined || this._backRoute === '') {
            this.routerService.navigateToHome();
        } else {
            if (this._backRoute.slice(0, 4) === 'http') {
                if (isPlatformBrowser(this.platformId)) {
                    window.location.href = this._backRoute;
                }
            } else {
                this.router.navigate([this._backRoute]);
            }
        }
    }

    //////////////////////
    //  QUERIES
    //////////////////////

    public get menus$(): Observable<MenuViewModel[]> {
        return this._menus$.asObservable();
    }

    public get menuCollapsed$(): Observable<boolean> {
        return this._menuCollapsed$.asObservable();
    }

    public get showBack$(): Observable<boolean> {
        return this._showBack$.asObservable();
    }

    //////////////////////
    //  PRIVATE UTILS
    //////////////////////

    private trackTmsLink(label: string, targetUrl: string): void {
        this.titleService
            .getTitle()
            .pipe(first())
            .subscribe((title: string) => {
                const linkData = {
                    track_page: this.formatLabel(title),
                    track_zone: 'menu',
                    track_nom: label,
                    track_cible: targetUrl,
                    track_type_evt: 'click',
                };
                this.tmsService.trackLink(linkData);
            });
    }

    /**
     * Format label for TMS (remove accent, replace space by underscore
     * @param {string} label
     * @return {string}
     */
    private formatLabel(label: string): string {
        let formattedLabel: string = label.toLowerCase();
        const nonAsciisValues = {
            a: '[àáâãäå]',
            c: 'ç',
            e: '[èéêë]',
            i: '[ìíîï]',
            n: 'ñ',
            o: '[òóôõö]',
            u: '[ùúûűü]',
            y: '[ýÿ]',
        };
        for (const i in nonAsciisValues) {
            formattedLabel = formattedLabel.replace(new RegExp(nonAsciisValues[i], 'g'), i);
        }
        return formattedLabel.replace(' ', '_');
    }

    /**
     * Method called to update calculated values of the viewModel
     * Mainly updated by route change for now
     */
    private updateViewModel(): void {
        if (this._menus$.value.length) {
            this._menus$.next(
                this._menus$.value.map((menuItem: MenuViewModel) => {
                    if (
                        this.router.isActive(menuItem.path, {
                            paths: 'subset',
                            queryParams: 'subset',
                            fragment: 'ignored',
                            matrixParams: 'ignored',
                        })
                    ) {
                        menuItem.ariaCurrent = 'page';
                        menuItem.active = true;
                    } else if (this.isMenuItemActive(menuItem.path)) {
                        menuItem.ariaCurrent = 'true';
                        menuItem.active = true;
                    } else {
                        menuItem.ariaCurrent = null;
                        menuItem.active = false;
                    }
                    return menuItem;
                }),
            );
        }
    }

    private isMenuItemActive(itemPath: string): boolean {
        if (this.route.firstChild && this.route.firstChild.snapshot) {
            // extract root-path from current route (via firstChild property)
            const currentRootPath: string = this.route.firstChild.snapshot.routeConfig.path;
            if (this._pathMatchingMap.has(itemPath)) {
                const paths: string[] = this._pathMatchingMap.get(itemPath);
                return paths.some((path: string) => {
                    return currentRootPath.indexOf(path) !== -1; // test only on root-path
                });
            }
        }
        return false;
    }
}
